import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { GitCompare } from 'lucide-react'
import { AppShell } from '@/shared/ui/AppShell'
import { useTopicCompare } from '../state/useTopicCompare'
import { TopicStatsCard } from './TopicStatsCard'
import { DualLineChart } from './DualLineChart'

function TopicSelect({
  label,
  value,
  onChange,
  options,
  disabledValue,
}: {
  label: string
  value: string
  onChange: (slug: string) => void
  options: { id: string; name: string; slug: string }[]
  disabledValue: string
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30"
      >
        <option value="" />
        {options.map((t) => (
          <option
            key={t.id}
            value={t.slug || t.id}
            disabled={t.slug === disabledValue || t.id === disabledValue}
          >
            {t.name}
          </option>
        ))}
      </select>
    </div>
  )
}

export const TopicComparePage = memo(function TopicComparePage() {
  const { t } = useTranslation('compare')

  const {
    allTopics,
    slugA,
    slugB,
    statsA,
    statsB,
    isLoadingTopics,
    isLoadingA,
    isLoadingB,
    setSlugA,
    setSlugB,
  } = useTopicCompare()

  const showChart = (statsA?.series.length ?? 0) > 0 || (statsB?.series.length ?? 0) > 0

  return (
    <AppShell showAnalytics={false}>
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 lg:px-6">
        <div className="flex items-center gap-2">
          <GitCompare size={20} className="text-primary" />
          <h1 className="text-lg font-bold text-foreground">{t('title')}</h1>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TopicSelect
            label={t('selectA')}
            value={slugA}
            onChange={setSlugA}
            options={allTopics}
            disabledValue={slugB}
          />
          <TopicSelect
            label={t('selectB')}
            value={slugB}
            onChange={setSlugB}
            options={allTopics}
            disabledValue={slugA}
          />
        </div>

        {isLoadingTopics ? null : (
          <>
            <div className="flex flex-col gap-4 sm:flex-row">
              <TopicStatsCard
                stats={statsA}
                variant="a"
                loading={!!slugA && isLoadingA}
              />
              <TopicStatsCard
                stats={statsB}
                variant="b"
                loading={!!slugB && isLoadingB}
              />
            </div>

            {showChart && statsA && statsB && (
              <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <DualLineChart
                  seriesA={statsA.series}
                  seriesB={statsB.series}
                  labelA={statsA.name}
                  labelB={statsB.name}
                />
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  )
})
